import { Component, computed, ElementRef, inject, input, output, viewChild } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { UiDividerComponent, UIIconComponent, UiNumberComponent } from '@design-system';
import { OrderSide } from '@shared-types';
import { LongPressDirective } from '@tools';
import { PortfolioAsset } from '../../../../models/portfolio.model';
import { MASKED_VALUE } from '../../data-access/portfolio-view-state.service';

/**
 * One portfolio holding, as an asset card.
 *
 * Per the handoff annotation the card has two targets: the card body goes to the
 * symbol page, and the three-dot button raises {@link detailRequested} for the
 * holding's portfolio detail. The two never fire together, but that's arbitrated
 * with a press flag rather than stopping event propagation — the list container's
 * `SwipeRightLeftDirective` needs to see the button's touchend/mouseup too, to
 * finish closing a swiped-open row.
 *
 * The card body is a plain div rather than an anchor, and the symbol name carries
 * the real link — nesting the three-dot `button` inside an anchor would be
 * interactive-inside-interactive and fail the axe `nested-interactive` rule. The
 * swipe actions sit behind the card rather than inside it, so they are a real
 * anchor and a real button.
 *
 * The body listens for `shortClick` rather than `click`: the row is swipeable, and
 * the swipe directive suppresses nothing, so a raw click would navigate away in the
 * middle of the reveal gesture. {@link LongPressDirective} drops any tap that moved
 * further than its threshold.
 */
@Component({
	selector: 'app-portfolio-card-item',
	imports: [UiDividerComponent, UIIconComponent, UiNumberComponent, RouterLink, LongPressDirective],
	templateUrl: './portfolio-card-item.component.html',
	host: {
		class: 'block w-full'
	}
})
export class PortfolioCardItemComponent {
	readonly asset = input.required<PortfolioAsset>();

	/** The order form reads `:orderSide` as the numeric `OrderSide`, not a name. */
	readonly OrderSide = OrderSide;
	readonly router = inject(Router);
	/**
	 * The three-dot action. Consumers route this to the portfolio detail view;
	 * that screen does not exist yet, so the list currently ignores it.
	 */
	readonly amountsHidden = input(false);

	readonly maskedValue = MASKED_VALUE;

	/** The three-dot action. Consumers route this to the holding's detail screen. */
	readonly detailRequested = output<PortfolioAsset>();

	private readonly swipeableCard = viewChild.required<ElementRef<HTMLElement>>('swipeableCard');

	/**
	 * `shortClick` is raised from the swipeable card's own touchend/mouseup, and the
	 * detail button's tap passes through those same events on its way up. Stopping
	 * propagation there (as before) also kept `SwipeRightLeftDirective`'s touchend
	 * from ever reaching the list container, leaving a swiped-open row stuck. Letting
	 * the events bubble and suppressing just the resulting navigation fixes that.
	 */

	readonly isPriceChangePositive = computed(() => this.asset().priceChangePercent >= 0);
	/** An unchanged price is painted neutral rather than as a gain. */
	readonly isPriceChangeUnchanged = computed(() => this.asset().priceChangePercent === 0);
	readonly isProfit = computed(() => this.asset().profitLoss >= 0);

	/** Percentages render to two decimals, matching the handoff's `(-1.02%)`. */
	readonly formattedPriceChangePercent = computed(() => formatPercent(this.asset().priceChangePercent));
	readonly formattedProfitLossPercent = computed(() => formatPercent(this.asset().profitLossPercent));

	onDetailClick(): void {
		this.detailRequested.emit(this.asset());
	}

	onSellClick(): void {
		// TODO: Wire up the real sell flow once EAT-12337 lands.
		this.closeSwipe();
	}

	/**
	 * The swipe directive only snaps a row back when the tap lands outside the list,
	 * and this button sits inside it — so a completed action has to close the row itself.
	 */
	private closeSwipe(): void {
		const card = this.swipeableCard().nativeElement;
		card.style.transitionDuration = '0.3s';
		card.style.transform = 'translate3d(0px, 0px, 0px)';
	}
}

function formatPercent(value: number): string {
	return `(${value.toFixed(2)}%)`;
}
